import React, { useState, useEffect, useRef } from 'react';
import api from '../api';

function formatUptime(secs) {
  if (!secs) return '0:00';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  return `${m}:${String(s).padStart(2,'0')}`;
}

export default function PublicLive() {
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playerState, setPlayerState] = useState('idle');
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [uptime, setUptime] = useState(0);
  const [quality, setQuality] = useState('auto');
  const [availableQualities, setAvailableQualities] = useState([]);

  const loadLiveData = async () => {
    try {
      const { data } = await api.get('/public/live');
      setLiveData(data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadLiveData();
    const t = setInterval(loadLiveData, 10000);
    return () => clearInterval(t);
  }, []);

  // Uptime counter
  useEffect(() => {
    if (!liveData?.live) return;
    setUptime(liveData.uptimeSeconds || 0);
    const t = setInterval(() => setUptime(u => u + 1), 1000);
    return () => clearInterval(t);
  }, [liveData?.live]);

  // Check ABR variants using live status endpoint
  useEffect(() => {
    if (!liveData?.live || liveData?.isFallback) return;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch('/api/abr/live/variants');
        const data = await res.json();
        const q = ['auto'];
        if (data.has1080p) q.push('1080p');
        if (data.has720p) q.push('720p');
        if (data.has480p) q.push('480p');
        setAvailableQualities(q);
      } catch {}
    }, 8000);
    return () => clearTimeout(timer);
  }, [liveData?.live, liveData?.isFallback]);

  // HLS player
  useEffect(() => {
    if (!liveData?.live || !liveData.hlsUrl) { setPlayerState('idle'); return; }
    setPlayerState('loading');
    let destroyed = false;

    const init = async () => {
      await new Promise(r => setTimeout(r, 4000));
      if (destroyed) return;

      if (!window.Hls) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.5.7/hls.min.js';
        await new Promise((res, rej) => { script.onload = res; script.onerror = rej; document.head.appendChild(script); });
      }

      const Hls = window.Hls;
      const video = videoRef.current;
      if (!video || destroyed) return;

      // Pick URL based on quality - all routes through /live/ endpoints
      let url = liveData.hlsUrl || 'https://dfmtv.arnait.com/live/master.m3u8';
      if (quality === '720p') url = 'https://dfmtv.arnait.com/live/720p.m3u8';
      else if (quality === '480p') url = 'https://dfmtv.arnait.com/live/480p.m3u8';
      else url = 'https://dfmtv.arnait.com/live/master.m3u8';

      if (Hls.isSupported()) {
        if (hlsRef.current) hlsRef.current.destroy();
        const hls = new Hls({ liveSyncDurationCount: 2, lowLatencyMode: true });
        hls.loadSource(url);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (!destroyed) { setPlayerState('playing'); video.play().catch(()=>{}); }
        });
        hls.on(Hls.Events.ERROR, (e, data) => {
          if (data.fatal) {
            // Fallback to direct HLS if master playlist fails
            if (url !== liveData.hlsUrl) {
              hls.loadSource(liveData.hlsUrl);
            } else {
              setPlayerState('error');
            }
          }
        });
        hlsRef.current = hls;
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = url;
        video.play().catch(()=>{});
        setPlayerState('playing');
      }
    };

    init();
    return () => {
      destroyed = true;
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
  }, [liveData?.live, liveData?.hlsUrl, quality]);

  const s = {
    page: { minHeight: '100vh', background: '#0b0d10', display: 'flex', flexDirection: 'column', alignItems: 'center', fontFamily: 'Inter, sans-serif', color: '#e8eaf0' },
    header: { width: '100%', maxWidth: '900px', padding: '24px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    logo: { display: 'flex', alignItems: 'center', gap: '10px' },
    logoDot: (live) => ({ width: '10px', height: '10px', borderRadius: '50%', background: live ? '#00e676' : '#5c6b82', boxShadow: live ? '0 0 10px #00e676' : 'none', animation: live ? 'pulse-dot 1.4s infinite' : 'none' }),
    logoText: { fontSize: '18px', fontWeight: '800', letterSpacing: '-0.3px' },
    liveBadge: { fontSize: '11px', fontWeight: '800', padding: '4px 10px', borderRadius: '20px', background: 'rgba(0,230,118,0.12)', color: '#00e676', border: '1px solid rgba(0,230,118,0.25)' },
    offlineBadge: { fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '20px', background: 'rgba(92,107,130,0.15)', color: '#5c6b82', border: '1px solid rgba(92,107,130,0.2)' },
    main: { width: '100%', maxWidth: '900px', padding: '24px' },
    playerWrap: { position: 'relative', background: '#000', aspectRatio: '16/9', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' },
    video: { width: '100%', height: '100%', display: 'block' },
    overlay: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', textAlign: 'center', padding: '24px' },
    offlineScreen: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#141720', gap: '12px' },
    infoRow: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
    streamTitle: { fontSize: '20px', fontWeight: '700', letterSpacing: '-0.3px' },
    streamerName: { fontSize: '14px', color: '#8896ab' },
    uptime: { fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', color: '#5c6b82', marginLeft: 'auto' },
    platforms: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' },
    platPill: { fontSize: '11px', fontWeight: '700', padding: '3px 9px', borderRadius: '20px', background: 'rgba(0,230,118,0.08)', color: '#00e676', border: '1px solid rgba(0,230,118,0.2)' },
    footer: { marginTop: '32px', textAlign: 'center', color: '#5c6b82', fontSize: '12px', paddingBottom: '24px' },
    retryBtn: { padding: '8px 20px', background: '#00e676', color: '#000', fontWeight: '700', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', marginTop: '12px' },
  };

  if (loading) return (
    <div style={{ ...s.page, justifyContent: 'center' }}>
      <div style={{ color: '#5c6b82' }}>Loading…</div>
    </div>
  );

  if (liveData?.disabled) return (
    <div style={{ ...s.page, justifyContent: 'center' }}>
      <div style={{ color: '#5c6b82', textAlign: 'center' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>📡</div>
        <div style={{ fontWeight: '600', color: '#e8eaf0' }}>Live page is currently disabled</div>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&family=JetBrains+Mono&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes pulse-dot {
          0% { box-shadow: 0 0 0 0 rgba(0,230,118,0.6); }
          70% { box-shadow: 0 0 0 8px rgba(0,230,118,0); }
          100% { box-shadow: 0 0 0 0 rgba(0,230,118,0); }
        }
      `}</style>

      <div style={s.page}>
        <div style={s.header}>
          <div style={s.logo}>
            <div style={s.logoDot(liveData?.live)} />
            <span style={s.logoText}>{liveData?.stationName || 'MultiStream'}</span>
          </div>
          {liveData?.live
            ? <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <div style={s.liveBadge}>● ON AIR</div>
                {liveData?.viewers > 0 && <div style={{fontSize:'12px',color:'var(--text-muted)',fontWeight:'500'}}>{liveData.viewers} watching</div>}
              </div>
            : <div style={s.offlineBadge}>OFFLINE</div>}
        </div>

        <div style={s.main}>
          <div style={s.playerWrap}>
            <video ref={videoRef} style={s.video} controls playsInline />
            {/* Quality selector overlay — top right corner inside player */}
            {liveData?.live && availableQualities.length > 1 && (
              <div style={{position:'absolute',top:'10px',right:'10px',zIndex:10}}>
                <select
                  value={quality}
                  onChange={e => setQuality(e.target.value)}
                  style={{fontSize:'11px',fontWeight:'700',padding:'4px 8px',borderRadius:'6px',background:'rgba(0,0,0,0.65)',border:'1px solid rgba(255,255,255,0.2)',color:'#fff',cursor:'pointer',backdropFilter:'blur(4px)'}}
                >
                  {availableQualities.map(q => (
                    <option key={q} value={q} style={{background:'#141720'}}>
                      {q === 'auto' ? '⚡ Auto' : q}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!liveData?.live && (
              <div style={s.offlineScreen}>
                <div style={{ fontSize: '48px' }}>📡</div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#e8eaf0' }}>
                  {liveData?.stationName} is offline
                </div>
                <div style={{ fontSize: '14px', color: '#5c6b82' }}>Check back soon for live programming</div>
              </div>
            )}

            {liveData?.live && playerState === 'loading' && (
              <div style={{ ...s.overlay, background: 'rgba(0,0,0,0.8)' }}>
                <div style={{ fontSize: '32px' }}>⏳</div>
                <div style={{ fontWeight: '700', color: '#e8eaf0' }}>Connecting to stream…</div>
                <div style={{ fontSize: '13px', color: '#8896ab' }}>Please wait a moment</div>
              </div>
            )}

            {liveData?.live && playerState === 'error' && (
              <div style={{ ...s.overlay, background: 'rgba(0,0,0,0.8)' }}>
                <div style={{ fontSize: '32px' }}>⚠️</div>
                <div style={{ fontWeight: '700', color: '#e8eaf0' }}>Stream loading failed</div>
                <button style={s.retryBtn} onClick={() => setPlayerState('loading')}>Retry</button>
              </div>
            )}
          </div>

          {liveData?.live && (
            <>
              <div style={s.infoRow}>
                <div>
                  <div style={s.streamTitle}>
                    {liveData.streamTitle || liveData.stationName}
                  </div>
                  {liveData.streamerName && (
                    <div style={s.streamerName}>with {liveData.streamerName}</div>
                  )}
                </div>
                <div style={s.uptime}>{formatUptime(uptime)}</div>
              </div>

              {liveData.liveDestinations?.length > 0 && (
                <div style={s.platforms}>
                  {liveData.liveDestinations.map((d, i) => (
                    <div key={i} style={s.platPill}>● {d.label}</div>
                  ))}
                </div>
              )}
            </>
          )}

          {!liveData?.live && liveData?.stationTagline && (
            <div style={{ textAlign: 'center', marginTop: '16px', color: '#8896ab', fontSize: '14px' }}>
              {liveData.stationTagline}
            </div>
          )}
        </div>

        <div style={s.footer}>
          Powered by {liveData?.stationName} Broadcast Platform
        </div>
      </div>
    </>
  );
}
